import { useCallback, useEffect, useMemo, useRef } from "react";

import { usePostHog } from "posthog-js/react";

import { ClientAppEvent } from "../constants";
import { useRoleContext } from "./useRoleContext";
import { useWriterComponentState } from "./useWriterComponentState";

interface CarrierLanePreferencesBody {
  match_with_broker_shipments: boolean;
  match_only_with_partners: boolean;
}

interface Props {
  /**
   * Latest fetched preferences. The hook seeds its PATCH body from this so
   * partial updates (e.g. updating only one toggle) preserve the other field.
   * Pass `null` while loading; updates issued before preferences arrive will
   * write the field defaults (both `false`) for any field not in the partial.
   */
  preferences: CarrierLanePreferences | null;
  onUpdateFailure?: (error: Error) => void;
}

export function useCarrierLanePreferencesUpdate(props: Props) {
  const { preferences, onUpdateFailure } = props;
  const { role } = useRoleContext();
  const posthog = usePostHog();
  const bodyRef = useRef<CarrierLanePreferencesBody>({
    match_with_broker_shipments: false,
    match_only_with_partners: false,
  });

  useEffect(() => {
    if (preferences != null) {
      bodyRef.current.match_with_broker_shipments =
        preferences.match_with_broker_shipments;
      bodyRef.current.match_only_with_partners =
        preferences.match_only_with_partners;
    }
  }, [preferences]);

  const writeConfig = useMemo(
    () => ({
      method: "patch" as const,
      url: `/carriers/${role?.id}/lanes/update-preferences`,
    }),
    [role?.id],
  );

  const { loading, error, success, write } = useWriterComponentState({
    errorMessage: "Failed to update carrier lane preferences",
    writeConfig,
    errorMessageKey: "message",
    onWriteFailure: onUpdateFailure,
  });

  const updatePreferences = useCallback(
    (partial: Partial<CarrierLanePreferencesBody>) => {
      Object.assign(bodyRef.current, partial);
      write(() => {
        posthog?.capture(ClientAppEvent.CARRIER_LANE_PREFERENCES_UPDATED, {
          ...bodyRef.current,
        });
      }, bodyRef.current);
    },
    [write, posthog],
  );

  return {
    loading,
    error,
    success,
    updatePreferences,
  };
}
